/* eslint-disable react/prop-types */
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { observer } from 'mobx-react'
import { ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline'
import { DevicePhoneMobileIcon, PlusCircleIcon } from '@heroicons/react/24/solid'
import dayjs from 'dayjs'
import _ from 'lodash'
import { twMerge as mergeClassNames } from 'tailwind-merge'

// Components
import { AddUserModal } from '../../components/AddUserModal'
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { DataTile } from '../../components/DataTile'
import { EventHeader } from '../../components/EventHeader'
import { IdentificationIcon } from '../../components/IdentificationIcon'
import { LicenseQRModal } from '../../components/LicenseQRModal'
import { LicenseStatusBadge } from '../../components/LicenseStatusBadge'
import { LicenseTypeBadge } from '../../components/LicenseTypeBadge'
import { Modal } from '../../components/Modal'
import { PhoneNumberInput } from '../../components/PhoneNumberInput'
import { PurchaseLicensesModal } from '../../components/PurchaseLicensesModal'
import { PurchaseReceipts } from '../../components/PurchaseReceipts'
import { QRIcon } from '../../components/QRIcon'
import { Select } from '../../components/Select'
import { StateContainer } from '../../components/StateContainer'

// Images
import Edit from '../../assets/images/editCircle.svg'

// Stores
import { NavigationStoreContext } from '../../stores/NavigationStore'

// Service
import {
  getLicenses,
  updateLicense,
  updateLicenseAndCreateUser,
} from '../../services/licenses.service'
import { getExhibitor } from '../../services/exhibitors.service'
import { getOrganizationUsers } from '../../services/organizations.service'

// Utils
import { hasEventConcluded, hasEventStarted } from '../../utils/dates'
import { handlePagination, toast } from '../../utils/helpers'
import { getChargeData, getPricingData } from '../../components/PurchaseLicensesModal/helpers'

// Hooks
import usePurchaseTransaction from '../../hooks/PurchaseTransaction'

const DEFAULT = {
  assignee: '',
}

/**
 *
 * ExhibitorEventLicenses
 *
 */
const ExhibitorEventLicenses = observer(() => {
  // Context
  const { event, eventId, organization, organizationId } = useContext(NavigationStoreContext)

  // Hooks
  const { loadingPurchase, submitPurchase, repeatPurchaseDialog } = usePurchaseTransaction()

  // State
  const [loading, setLoading] = useState(true)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [licenses, setLicenses] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [users, setUsers] = useState([])
  const [licenseToUpdate, setLicenseToUpdate] = useState(null)
  const [showUserModal, setShowUserModal] = useState(false)
  const [loadingUser, setLoadingUser] = useState(false)
  const [reassignUser, setReassignUser] = useState(false)
  const [updatingLicense, setUpdatingLicense] = useState(false)
  const [showLicensesModal, setShowLicensesModal] = useState(false)
  const [showPhoneWarning, setShowPhoneWarning] = useState(false)
  const [pendingAssignment, setPendingAssignment] = useState(null)
  // The license whose login QR code is open, or null
  const [qrLicense, setQrLicense] = useState(null)

  // Tile counts, across every license rather than the loaded page
  const [totalLicenseCount, setTotalLicenseCount] = useState(0)
  const [unassignedLicenseCount, setUnassignedLicenseCount] = useState(0)
  const [rentalDeviceCount, setRentalDeviceCount] = useState(0)

  // Not a tile. The licenses the exhibitor bought, which is what decides whether the
  // first-license price applies — a free allotment does not use that price up.
  const [purchasedLicenseCount, setPurchasedLicenseCount] = useState(0)

  // License table pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [pages, setPages] = useState(null)

  // User dropdown pagination
  const [userSearchTerm, setUserSearchTerm] = useState(null)
  const [loadingNextUserPage, setLoadingNextUserPage] = useState(false)
  const [userCurrentPage, setUserCurrentPage] = useState(1)
  const [userNextPage, setUserNextPage] = useState(null)
  const [userTotalCount, setUserTotalCount] = useState(null)
  const [userPerPage] = useState(100)

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccesses = (m) => toast(m, 'success')

  // Once the event is over, exhibitor admins stop assigning and purchasing (EXEC-1607).
  // Read access is untouched: the tables, receipts and lead data stay available. The
  // boundary is read in the event's timezone, not the admin's browser — see
  // `hasEventConcluded`.
  const eventHasConcluded = hasEventConcluded(event)

  // Reassignment locks at the event's start date (EXEC-374), read in the event's own
  // timezone rather than the admin's browser — an admin travelling to the show would
  // otherwise cross the boundary at a different moment than the API does.
  const eventHasStarted = hasEventStarted(event)

  const LICENSES_URL = `/events/${eventId}/exhibitors/${event?.eventExhibitor?.id}/licenses/`

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: DEFAULT,
  })

  // Separate form for the phone number warning modal
  const {
    control: phoneControl,
    handleSubmit: handlePhoneSubmit,
    formState: { errors: phoneErrors },
    reset: resetPhone,
  } = useForm({
    defaultValues: { phoneNumber: '' },
  })

  /**
   * Gets the updated list of licenses; updates pagination.
   * @param {string} url
   */
  const getUpdatedLicenses = async (url) => {
    const response = await getLicenses(url, handleErrors, setLoading)

    if (response) {
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })
      setLicenses(response.results)
    }
  }

  /**
   * Gets the three tile counts.
   *
   * The license counts come from the API's `count` rather than the loaded page, which
   * only ever holds one page. Cancelled licenses are left out of both — they stay in the
   * table so their status is visible, but they aren't licenses the exhibitor has. The
   * rental count comes off the exhibitor itself, so it's re-read after a purchase rather
   * than going stale until the next navigation.
   */
  const getCounts = async () => {
    if (!event?.eventExhibitor?.id) return

    const countUrl = (filters) => `${LICENSES_URL}?is_cancelled=false&${filters}limit=1`

    const [all, unassigned, exhibitor] = await Promise.all([
      getLicenses(countUrl(''), handleErrors, () => {}),
      getLicenses(countUrl('is_assigned=false&'), handleErrors, () => {}),
      getExhibitor(eventId, event.eventExhibitor.id, handleErrors),
    ])

    if (all) setTotalLicenseCount(all.count)
    if (unassigned) setUnassignedLicenseCount(unassigned.count)
    if (exhibitor) {
      setRentalDeviceCount(exhibitor.rentalDeviceCount || 0)
      // Computed by the API from the whole set, so it stays right across pagination
      setPurchasedLicenseCount(exhibitor.licenseCounts?.numValidPaidLicenses || 0)
    }
  }

  const USER_BASE_URL = `/organizations/${organizationId}/roles/?expand=user&limit=${userPerPage}`

  /**
   * Filters out users that are already assigned to a usable (uncancelled) license.
   *
   * A refunded license still holds its assignee, since it carries on working.
   */
  const filterAssignableUsers = (roles) =>
    _.filter(roles, (role) => {
      const assignedUser = _.find(licenses, (license) =>
        license.assignee ? license.assignee.id === role.id && !license.isCancelled : false,
      )
      return !assignedUser
    })

  /**
   * Maps role objects to select option format.
   */
  const mapUsersToOptions = (roles) =>
    roles.map((user) => ({
      label: `${user.user.firstName} ${user.user.lastName}`,
      id: `${user.id}`,
      phoneNumber: user.user.phoneNumber,
    }))

  /**
   * Gets the updated list of organization users with pagination support.
   * @param {string} url
   * @param {boolean} firstPage - Whether this is the first page (resets the list)
   */
  const getUpdatedUserList = async (url, firstPage = false) => {
    const response = await getOrganizationUsers(
      url,
      handleErrors,
      firstPage ? setLoadingUsers : setLoadingNextUserPage,
      () => {},
    )

    if (response) {
      setUserTotalCount(response.count)
      setUserNextPage(response.next ? response.next.split('page=')[1] : null)

      const assignableUsers = filterAssignableUsers(response.results)
      const mappedUsers = mapUsersToOptions(assignableUsers)

      if (firstPage) {
        setUsers(mappedUsers)
      } else {
        setUsers((prev) => [...prev, ...mappedUsers])
      }
    }
  }

  /**
   * Performs the actual license assignment.
   * @param {object} data
   * @param {string} phoneNumber - Optional phone number to set on the user
   */
  const performAssignment = async (data, phoneNumber = null) => {
    const payload = {
      assignee: data.assignee ? data.assignee.id : null,
    }

    // If a phone number is provided, include it to be set on the user
    if (phoneNumber) {
      payload.assignee_phone_number = phoneNumber
    }

    await updateLicense(
      eventId,
      event.eventExhibitor.id,
      licenseToUpdate.id,
      payload,
      handleErrors,
      setUpdatingLicense,
      (m) => {
        handleSuccesses(m)
        getCounts()
        getUpdatedLicenses(
          `/events/${eventId}/exhibitors/${event.eventExhibitor.id}/licenses/?limit=${perPage}&expand=assignee`,
        )
        setReassignUser(false)
        setShowModal(false)
        setShowPhoneWarning(false)
        setPendingAssignment(null)
        reset(DEFAULT)
        resetPhone({ phoneNumber: '' })
      },
    )
  }

  /**
   * Handles submitting the assign license form.
   * Checks if the selected user has a phone number and prompts if not.
   * @param {object} data
   */
  const onSubmit = async (data) => {
    // If unassigning or user has a phone number, proceed directly
    if (!data.assignee || data.assignee.phoneNumber) {
      await performAssignment(data)
      return
    }

    // User doesn't have a phone number - show warning
    setPendingAssignment(data)
    setShowModal(false)
    setShowPhoneWarning(true)
  }

  /**
   * Handles submitting the add user form.
   * @param {object} data
   */
  const userModalOnSubmit = async (data) => {
    const updatedData = {
      new_user_first_name: data.firstName,
      new_user_last_name: data.lastName,
      new_user_email: data.email,
      new_user_phone_number: data.phoneNumber,
      new_user_role: data.role,
    }

    await updateLicenseAndCreateUser(
      eventId,
      event.eventExhibitor.id,
      licenseToUpdate.id,
      updatedData,
      handleErrors,
      setLoadingUser,
      async (m) => {
        getCounts()
        await getUpdatedLicenses(
          `/events/${eventId}/exhibitors/${event.eventExhibitor.id}/licenses/?limit=${perPage}&expand=assignee`,
        )
        handleSuccesses(m)
        setShowUserModal(false)
      },
    )
  }

  const submitPurchasingForm = async (data) => {
    const pricingInfo = getPricingData(event)
    // The count across every page, not `licenses`, which only ever holds the page on
    // screen. Paging to a run of cancelled rows made this read zero and quote the
    // first-license price, which the API then refused as a pricing mismatch.
    const chargeData = await getChargeData(data, pricingInfo, purchasedLicenseCount)

    await submitPurchase(eventId, event.eventExhibitor.id, chargeData, handleErrors, () => {
      handleSuccesses('Licenses purchased successfully')
      setShowLicensesModal(false)
      getCounts()
      getUpdatedLicenses(
        `/events/${eventId}/exhibitors/${event.eventExhibitor.id}/licenses/?limit=${perPage}&expand=assignee`,
      )
    })
  }

  /**
   * When the row count changes, get the updated licenses list.
   */
  useEffect(() => {
    if (event?.eventExhibitor && event?.eventExhibitor.id) {
      getCounts()
      getUpdatedLicenses(
        `/events/${eventId}/exhibitors/${event.eventExhibitor.id}/licenses/?limit=${perPage}&expand=assignee`,
      )
    }
  }, [event?.eventExhibitor, perPage])

  /**
   * When the user search term changes, fetch the first page of results.
   */
  useEffect(() => {
    if (!showModal) return () => {}

    let timeout
    if (userSearchTerm) {
      setUserCurrentPage(1)
      timeout = setTimeout(() => {
        getUpdatedUserList(`${USER_BASE_URL}&page=1&q=${userSearchTerm}`, true)
      }, 650)
    } else if (userSearchTerm === '') {
      // Search was cleared — reload all users
      setUserCurrentPage(1)
      getUpdatedUserList(`${USER_BASE_URL}&page=1`, true)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [userSearchTerm])

  const debounceUserSearch = useCallback(_.debounce(setUserSearchTerm, 650), [])

  const clearForm = () => {
    reset(DEFAULT)
  }

  /**
   * Configures the assignee options for the select.
   *
   * Since we remove users that have already been assigned to a license,
   * we need to add the current assignee back to the list to ensure it can be unassigned.
   * @returns {array} An array of users that can be assigned to a license.
   */
  const configureAssigneeOptions = () => {
    if (licenseToUpdate.assignee) {
      return _.concat(users, {
        label: `${licenseToUpdate.assignee.user.firstName} ${licenseToUpdate.assignee.user.lastName}`,
        id: `${licenseToUpdate.assignee.id}`,
        phoneNumber: licenseToUpdate.assignee.user.phoneNumber,
      })
    }

    return users
  }

  /**
   * Renders a detail of the license's assignee, or a dash where there is nothing to show —
   * an unassigned license, or an assignee we hold no phone number for.
   * @param {string} value
   */
  const renderAssigneeDetail = (value) =>
    value ? (
      <span className="font-medium text-black">{value}</span>
    ) : (
      <span className="text-gray-500">—</span>
    )

  /**
   * Renders the license assignee or a button to assign a license.
   * @param {object} assignee
   * @param {object} license
   */
  const renderAssignee = (assignee, license) => {
    // Cancelling now releases the assignee (EXEC-1600), so a cancelled license reaches
    // this unassigned branch. It isn't one to hand out — the API rejects the assignment,
    // and even if it didn't, the login would fail in Fetch. The row still lists, so its
    // Cancelled badge stays visible.
    if (license.isCancelled) {
      return <span className="text-gray-500">—</span>
    }

    if (!assignee) {
      if (eventHasConcluded) {
        return <span className="text-gray-500">—</span>
      }

      return (
        <button
          type="button"
          className="space-between flex items-center gap-1 font-medium text-black"
          onClick={async () => {
            setUserSearchTerm(null)
            setUserCurrentPage(1)
            setUserNextPage(null)
            await getUpdatedUserList(`${USER_BASE_URL}&page=1`, true)
            setLicenseToUpdate(license)
            setShowModal(true)
          }}
        >
          <PlusCircleIcon className="h-5 fill-purple" />
          Assign license
        </button>
      )
    }

    return (
      <div className="space-between flex items-center gap-4">
        <span className={mergeClassNames('text-gray-500', license.isCancelled && 'line-through')}>
          {assignee.user.firstName} {assignee.user.lastName}
        </span>
        {assignee.user.isInvited && assignee.user.lastLogin === null && (
          <div className="rounded-full bg-status-blue px-2.5 py-0.5">
            <span className="text-xs font-bold text-status-darkBlue">Invite Pending</span>
          </div>
        )}
        {!eventHasStarted && !license.isCancelled && (
          <button
            className="px-2 py-0.5 font-bold text-purple hover:rounded-full hover:bg-status-purple disabled:opacity-50"
            type="button"
            onClick={async () => {
              setUserSearchTerm(null)
              setUserCurrentPage(1)
              setUserNextPage(null)
              await getUpdatedUserList(`${USER_BASE_URL}&page=1`, true)

              reset({
                assignee: {
                  label: `${assignee.user.firstName} ${assignee.user.lastName}`,
                  id: assignee.id,
                  phoneNumber: assignee.user.phoneNumber,
                },
              })

              setLicenseToUpdate(license)
              setReassignUser(true)
              setShowModal(true)
            }}
          >
            Reassign
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="h-full w-full">
      <StateContainer>
        <div className="relative flex h-full w-full flex-col space-y-3 overflow-y-auto p-3">
          <EventHeader event={event} />

          {/* Counts an exhibitor admin comes to this page to check. Shown whatever the
              numbers are, including no rentals — an absent tile reads as "we don't track
              that" rather than "you rented none". Mirrors the KPI tiles EE staff see on
              the exhibitor detail page. */}
          <div className="flex w-full flex-col space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0">
            <DataTile
              color="blue"
              icon={<IdentificationIcon className="h-6 fill-white" />}
              label="Unassigned Licenses"
              value={unassignedLicenseCount}
            />

            <DataTile
              color="green"
              icon={<IdentificationIcon className="h-6 fill-white" />}
              label="Total Licenses"
              value={totalLicenseCount}
            />

            <DataTile
              color="purple"
              icon={<DevicePhoneMobileIcon className="h-6 fill-white" />}
              label="Devices Rented"
              value={rentalDeviceCount}
            />
          </div>

          {/* Says why the actions below are gone, rather than leaving an admin to work it
              out from a missing button */}
          {eventHasConcluded && (
            <div className="rounded-2xl bg-info-light px-4 py-3 text-sm text-info">
              <span className="font-bold">{event?.name} has ended.</span> Licenses can no longer be
              assigned or purchased for it. Your licenses, receipts and lead data are still here —
              contact Executivevents if you need a change.
            </div>
          )}

          <div className="mb-4 flex w-full flex-row place-items-center justify-between">
            <span className="text-md font-semibold">Licenses</span>

            {!eventHasConcluded && (
              <Button
                background="bg-purple border-purple hover:bg-purple-600"
                icon={<PlusIcon className="h-5 fill-white sm:h-6" />}
                label="Purchase Additional Licenses"
                onClick={() => setShowLicensesModal(true)}
              />
            )}
          </div>
          <div className="grid w-full">
            <DataTable
              columns={[
                {
                  id: 'name',
                  grow: 1.5,
                  name: 'Licenses ID',
                  selector: (row) => row.id,
                  cell: (row) => (
                    <>
                      <span
                        className={mergeClassNames(
                          'font-medium text-black',
                          row.isCancelled && 'text-gray-500',
                        )}
                      >
                        {row.licenseId}
                      </span>
                      {row.status !== 'Active' && (
                        <span className="ml-4">
                          <LicenseStatusBadge status={row.status} />
                        </span>
                      )}
                    </>
                  ),
                  minWidth: '260px',
                },
                {
                  id: 'staff-assigned',
                  grow: 1.5,
                  name: 'Staff Assigned',
                  selector: (row) => row.assignee,
                  cell: (row) => renderAssignee(row.assignee, row),
                  minWidth: '280px',
                },
                {
                  id: 'email',
                  grow: 1.5,
                  name: 'Email',
                  selector: (row) => row.assignee,
                  cell: (row) => renderAssigneeDetail(row.assignee?.user?.email),
                  minWidth: '230px',
                },
                {
                  id: 'phone',
                  grow: 1,
                  name: 'Phone',
                  selector: (row) => row.assignee,
                  cell: (row) => renderAssigneeDetail(row.assignee?.user?.phoneNumber),
                  minWidth: '150px',
                },
                {
                  id: 'type',
                  grow: 0.5,
                  name: 'Type',
                  selector: (row) => row.isFreeLicense,
                  cell: (row) => <LicenseTypeBadge isFreeLicense={row.isFreeLicense} />,
                  minWidth: '100px',
                },
                {
                  id: 'qr',
                  name: '',
                  center: true,
                  width: '90px',
                  // Booth staff get logged in by scanning, without EE having to pull the
                  // link for them (EXEC-1609). Only where there's an assignee to log in
                  // as, and never on a cancelled license — the link would resolve to a
                  // login that fails in Fetch. The API scopes licenses to the caller's own
                  // exhibitor, so an admin can only ever reach their own team's codes.
                  cell: (row) =>
                    row.assignee &&
                    !row.isCancelled && (
                      <button
                        className="flex flex-row items-center gap-1 whitespace-nowrap px-2 py-0.5 font-bold text-purple hover:rounded-full hover:bg-status-purple"
                        onClick={() => setQrLicense(row)}
                        type="button"
                      >
                        <QRIcon className="h-4 shrink-0 fill-purple" />
                        QR
                      </button>
                    ),
                },
              ]}
              data={licenses}
              defaultSortFieldId="dates"
              defaultSortAsc
              onChangePage={(page) =>
                handlePagination(
                  page,
                  currentPage,
                  perPage,
                  totalRows,
                  pages,
                  setCurrentPage,
                  getUpdatedLicenses,
                  `/events/${eventId}/exhibitors/${event.eventExhibitor.id}/licenses/?expand=assignee&limit=`,
                )
              }
              onChangeRowsPerPage={async (currentRowsPerPage) => setPerPage(currentRowsPerPage)}
              pagination
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[5, 10, 15, 20, 30, 50]}
              paginationTotalRows={totalRows}
              paginationServer
              progressPending={loading}
              sortServer
            />
          </div>

          {event?.eventExhibitor?.id && (
            <PurchaseReceipts
              event={event}
              eventExhibitorId={event.eventExhibitor.id}
              organizationName={organization?.name}
            />
          )}
        </div>
      </StateContainer>

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => {
              reset(DEFAULT)
              setReassignUser(false)
            },
          },
          {
            type: 'submit',
            label: reassignUser ? 'Reassign' : 'Assign',
            onClick: handleSubmit(onSubmit),
          },
        ]}
        icon={
          reassignUser ? (
            <img src={Edit} alt="Reassign" />
          ) : (
            <PlusIcon className="h-5 stroke-white sm:h-6" />
          )
        }
        content={
          <div className="mt-3 flex flex-col space-y-4 text-center sm:mt-5">
            <span className="text-left font-nunito font-medium">
              You can reassign licenses until: {dayjs(event?.startsAt).format('M/D/YYYY')}
            </span>

            <Controller
              name="assignee"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  pagination={{
                    attemptToLoadNextPage: async () => {
                      if (
                        !loadingNextUserPage &&
                        userNextPage &&
                        userNextPage !== userCurrentPage
                      ) {
                        setUserCurrentPage(userNextPage)
                        const url = userSearchTerm
                          ? `${USER_BASE_URL}&page=${userNextPage}&q=${userSearchTerm}`
                          : `${USER_BASE_URL}&page=${userNextPage}`
                        getUpdatedUserList(url)
                      }
                    },
                    hasNext: userNextPage !== null,
                    loading: loadingNextUserPage,
                    perPage: userPerPage,
                    totalCount: userTotalCount,
                  }}
                  className="rounded-2xl border-gray-550 py-2.5 pr-4  placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  disabled={loading}
                  error={errors.assignee && 'This field is required'}
                  fullWidth
                  hoverElement={
                    <Button
                      label="Unassign"
                      onClick={clearForm}
                      className="border-transparent bg-transparent py-0 leading-none hover:bg-transparent sm:py-0"
                    />
                  }
                  id="assignee"
                  name="assignee"
                  nunito
                  onChange={onChange}
                  options={configureAssigneeOptions()}
                  label=""
                  placeholder="Search for a Staff Member"
                  reset={clearForm}
                  search
                  setSearchTerm={debounceUserSearch}
                  style={{ flex: true, width: '100%' }}
                  value={value}
                />
              )}
            />

            <button
              type="button"
              className="space-between flex items-center gap-1 font-nunito font-medium text-black"
              onClick={() => {
                setShowModal(false)
                setShowUserModal(true)
              }}
            >
              <PlusCircleIcon className="h-5 fill-purple" />
              Create a New User
            </button>
          </div>
        }
        loading={loadingUsers || updatingLicense}
        onClose={() => {
          setShowModal(false)
          reset(DEFAULT)
          setReassignUser(false)
        }}
        open={showModal}
        setOpen={setShowModal}
        title={reassignUser ? 'Reassign License' : 'Assign License'}
      />

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Skip',
            onClick: () => {
              if (pendingAssignment) {
                performAssignment(pendingAssignment)
              }
            },
          },
          {
            type: 'submit',
            label: 'Save & Assign',
            onClick: handlePhoneSubmit((data) => {
              if (pendingAssignment) {
                performAssignment(pendingAssignment, data.phoneNumber)
              }
            }),
          },
        ]}
        icon={<ExclamationTriangleIcon className="h-6 stroke-white" />}
        content={
          <div className="mt-3 flex flex-col space-y-4 text-center sm:mt-5">
            <p className="text-left font-nunito">
              <span className="font-bold">{pendingAssignment?.assignee?.label}</span> does not have
              a phone number set. Enter their phone number below, or skip to assign without one.
            </p>

            <PhoneNumberInput
              control={phoneControl}
              disabled={updatingLicense}
              error={phoneErrors.phoneNumber}
              label="Phone Number"
              name="phoneNumber"
              placeholder="Phone Number"
              required
            />
          </div>
        }
        loading={updatingLicense}
        onClose={() => {
          setShowPhoneWarning(false)
          setPendingAssignment(null)
          resetPhone({ phoneNumber: '' })
        }}
        open={showPhoneWarning}
        setOpen={setShowPhoneWarning}
        title="Add Phone Number"
      />

      {showUserModal && (
        <AddUserModal
          endOfFormContent={`You can reassign licenses until: ${dayjs(event.startsAt).format(
            'M/D/YYYY',
          )}`}
          loadingUser={loadingUser}
          onSubmit={userModalOnSubmit}
          requirePhoneNumber
          roles={{
            allowRoleChange: true,
            key: 'role',
            options: [
              {
                label: 'Admin',
                description:
                  'Admins can access all Events and manage Qualifiers and Materials. Admins can also add, edit, and delete other Users.',
              },
              {
                label: 'Booth Staff',
                description:
                  'Booth Staff will only have access to Events they are assigned Licenses for Booth Staff can be managed from the Event License page.',
              },
            ],
            showRoleDescriptions: true,
          }}
          setEditUser={() => {}}
          showRoleDescriptions
          showUserModal={showUserModal}
          setShowUserModal={setShowUserModal}
        />
      )}

      {qrLicense && (
        <LicenseQRModal
          license={qrLicense}
          showModal={!!qrLicense}
          setShowModal={() => setQrLicense(null)}
        />
      )}

      {showLicensesModal && (
        <PurchaseLicensesModal
          event={event}
          heldLicenseCount={totalLicenseCount}
          purchasedLicenseCount={purchasedLicenseCount}
          existingRentalDeviceCount={rentalDeviceCount}
          loading={loadingPurchase}
          onSubmit={submitPurchasingForm}
          showModal={showLicensesModal}
          setShowModal={setShowLicensesModal}
        />
      )}

      {repeatPurchaseDialog}
    </div>
  )
})

export default ExhibitorEventLicenses
